import { Directive, input } from '@angular/core';

export type ButtonVariant = 'solid' | 'underline' | 'ghost' | 'subtle' | 'tile' | 'outline';
export type ButtonTheme = 'dark' | 'light';
export type ButtonSize = 'default' | 'small';
export type ButtonWidth = 'full' | 'auto';

/**
 * Button directive — extends native `<button>` and `<a>` elements.
 * Selector: `button[mst-button], a[mst-button]`
 *
 * Variants : solid | underline | ghost | subtle | tile | outline
 * Themes   : dark (default) | light
 * Size     : standard (default) | small
 *
 * @example
 * <button mst-button variant="solid">Scopri i programmi</button>
 * <button mst-button variant="outline" theme="light">Annulla</button>
 * <button mst-button variant="ghost" size="small">Leggi di più</button>
 * <button mst-button variant="underline"><span>Invia di nuovo</span></button>
 * <a mst-button variant="solid" href="/path">Link styled as button</a>
 */
@Directive({
    selector: 'button[mst-button], a[mst-button]',
    standalone: true,
    host: {
        class: 'mst-button',
        '[class.-solid]': 'variant() === "solid"',
        '[class.-underline]': 'variant() === "underline"',
        '[class.-ghost]': 'variant() === "ghost"',
        '[class.-subtle]': 'variant() === "subtle"',
        '[class.-tile]': 'variant() === "tile"',
        '[class.-outline]': 'variant() === "outline"',
        '[class.-light]': 'theme() === "light"',
        '[class.-small]': 'size() === "small"',
        '[class.-full]': 'width() === "full"',
        '[class.-auto]': 'width() === "auto"',
        // '[class.-loading]': 'loading()',
        // '[attr.disabled]': 'disabled() || loading() || null',
        // '[attr.aria-disabled]': 'disabled() || loading()',
        // '[attr.aria-busy]': 'loading()',
    },
})
export class ButtonDirective {
    /** Visual style variant. Must always be specified explicitly. */
    readonly variant = input<ButtonVariant>();

    /** Color theme. Default: dark. */
    readonly theme = input<ButtonTheme>('dark');

    /** Size. Default: default. */
    readonly size = input<ButtonSize>('default');

    /** Width behaviour. `full` = 100%, `auto` = fit content. Default: auto. */
    readonly width = input<ButtonWidth>('auto');

    // /** Disables the button. */
    // readonly disabled = input(false);

    // /** Shows a loading state. */
    // readonly loading = input(false);
}
